import apiClient from './api';
import type { EquipeDeneignement, User, PaginatedResponse } from '@/types';

const BASE_URL = '/equipes';

export interface CreateEquipeDTO {
  nom: string;
  code: string;
  chefId?: number;
}

export interface UpdateEquipeDTO extends Partial<CreateEquipeDTO> {
  actif?: boolean;
}

export interface EquipeStats {
  totalInterventions: number;
  interventionsEnCours: number;
  interventionsTerminees: number;
  tempsMoyenMinutes: number;
}

export interface AgentWithEquipe {
  id: number;
  nom: string;
  prenom: string;
  email: string;
  telephone?: string;
  nomComplet: string;
  equipeActuelle?: {
    id: number;
    nom: string;
  } | null;
}

/**
 * Service API pour les equipes de deneigement
 */
export const equipeService = {
  /**
   * Liste des equipes
   */
  async list(): Promise<EquipeDeneignement[]> {
    const response = await apiClient.get<EquipeDeneignement[]>(BASE_URL);
    return response.data;
  },

  /**
   * Detail d'une equipe
   */
  async get(id: number): Promise<EquipeDeneignement> {
    const response = await apiClient.get<EquipeDeneignement>(`${BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Creation d'une equipe
   */
  async create(data: CreateEquipeDTO): Promise<EquipeDeneignement> {
    const response = await apiClient.post<EquipeDeneignement>(BASE_URL, {
      nom: data.nom,
      code: data.code,
      chef_id: data.chefId,
    });
    return response.data;
  },

  /**
   * Modification d'une equipe
   */
  async update(id: number, data: UpdateEquipeDTO): Promise<EquipeDeneignement> {
    const payload: Record<string, unknown> = {};
    if (data.nom !== undefined) payload.nom = data.nom;
    if (data.code !== undefined) payload.code = data.code;
    if (data.chefId !== undefined) payload.chef_id = data.chefId;
    if (data.actif !== undefined) payload.actif = data.actif;

    const response = await apiClient.put<EquipeDeneignement>(`${BASE_URL}/${id}`, payload);
    return response.data;
  },

  /**
   * Suppression d'une equipe
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE_URL}/${id}`);
  },

  /**
   * Equipes disponibles
   */
  async getDisponibles(): Promise<EquipeDeneignement[]> {
    const response = await apiClient.get<EquipeDeneignement[]>(`${BASE_URL}/disponibles`);
    return response.data;
  },

  /**
   * Agents disponibles (non affectes)
   */
  async getAgentsDisponibles(): Promise<User[]> {
    const response = await apiClient.get<User[]>(`${BASE_URL}/agents/disponibles`);
    return response.data;
  },

  /**
   * Tous les agents de deneignement (avec info equipe actuelle)
   */
  async getAllAgents(): Promise<AgentWithEquipe[]> {
    const response = await apiClient.get<AgentWithEquipe[]>(`${BASE_URL}/agents/all`);
    return response.data;
  },

  /**
   * Ajouter un agent a une equipe
   */
  async addAgent(equipeId: number, agentId: number): Promise<EquipeDeneignement> {
    const response = await apiClient.post<EquipeDeneignement>(
      `${BASE_URL}/${equipeId}/agents`,
      { agent_id: agentId }
    );
    return response.data;
  },

  /**
   * Retirer un agent d'une equipe
   */
  async removeAgent(equipeId: number, agentId: number): Promise<EquipeDeneignement> {
    const response = await apiClient.delete<EquipeDeneignement>(
      `${BASE_URL}/${equipeId}/agents/${agentId}`
    );
    return response.data;
  },

  /**
   * Statistiques d'une equipe
   */
  async getStats(id: number): Promise<EquipeStats> {
    const response = await apiClient.get<EquipeStats>(`${BASE_URL}/${id}/stats`);
    return response.data;
  },
};

export default equipeService;
